import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Avatar,
  Rating,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Language,
  Schedule,
  Pets,
  Verified,
  Directions,
  CalendarToday,
  AttachMoney as Donate,
  VolunteerActivism,
  Facebook,
  Instagram,
  YouTube
} from '@mui/icons-material';
import { getPublicShelterById, getPublicShelterAnimals } from '@services/publicSheltersService';

interface Shelter {
  id: string;
  name: string;
  description: string;
  city: string;
  county: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  coordinates: { lat: number; lng: number } | null;
  working_hours: any;
  capacity: number;
  current_occupancy: number;
  status: string;
  animal_count: number;
  created_at: string;
}

interface Animal {
  id: string;
  name: string;
  species: string; // Standardizat: dog, cat, bird, rabbit, horse, other
  breed: string;
  birth_date: string;
  size: string;
  gender: string; // Standardizat: male, female (renumit din sex)
  sterilized: boolean;
  vaccinated: boolean;
  status: string; // Standardizat: available, reserved, adopted
  description: string;
  photos: string[];
  created_at: string;
  // Pentru compatibilitate
  sex?: string; // Deprecated
}

const ShelterPublicPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const [shelter, setShelter] = useState<Shelter | null>(null);
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [visitForm, setVisitForm] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    animalId: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    adoptionInfo: false
  });

  const fetchShelterData = useCallback(async () => {
    try {
      setLoading(true);
      const [shelterData, animalsData] = await Promise.all([
        getPublicShelterById(shelterId!),
        getPublicShelterAnimals(shelterId!, { status: 'available', limit: 12 })
      ]);
      
      setShelter(shelterData.data);
      setAnimals(animalsData.data.items || []);
    } catch (err: any) {
      setError(err.message || 'Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [shelterId]);

  useEffect(() => {
    if (shelterId) {
      fetchShelterData();
    }
  }, [shelterId, fetchShelterData]);

  const handleVisitSubmit = () => {
    // TODO: Implement visit request submission
    // Form submission logic will be implemented here
    setVisitDialogOpen(false);
    setVisitForm({ name: '', email: '', phone: '', date: '', time: '', animalId: '' });
  };

  const handleContactSubmit = () => {
    // TODO: Implement contact form submission
    // Form submission logic will be implemented here
    setContactDialogOpen(false);
    setContactForm({ name: '', email: '', message: '', adoptionInfo: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !shelter) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Adapostul nu a fost găsit'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Header / Copertă */}
      <Box
        sx={{
          height: '400px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar
              sx={{ width: 120, height: 120, border: '4px solid white' }}
              src="/api/placeholder/120/120"
            >
              {shelter.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h3" component="h1" gutterBottom>
                {shelter.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Chip 
                  label={shelter.status === 'active' ? 'Adopții active' : 'Temporar închis'} 
                  color={shelter.status === 'active' ? 'success' : 'warning'}
                  icon={<Pets />}
                />
                <Chip 
                  label="Verificat" 
                  color="primary" 
                  icon={<Verified />}
                />
                <Rating value={4.8} precision={0.1} readOnly size="small" />
                <Typography variant="body2">(120 recenzii)</Typography>
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Pets />}
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  Vezi animalele disponibile
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<CalendarToday />}
                  sx={{ borderColor: 'white', color: 'white' }}
                  onClick={() => setVisitDialogOpen(true)}
                >
                  Programează o vizită
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Phone />}
                  sx={{ borderColor: 'white', color: 'white' }}
                  onClick={() => setContactDialogOpen(true)}
                >
                  Contactează adapostul
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" gap={4}>
          {/* Descriere generală */}
          <Box flex={2}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Despre {shelter.name}
              </Typography>
              <Typography variant="body1" paragraph>
                {shelter.description || 'Fondată în 2010, misiunea noastră este să oferim o a doua șansă animalelor abandonate și să le găsim familii iubitoare. Ne dedicăm cu pasiune salvării, îngrijirii și adoptării animalelor din comunitatea noastră.'}
              </Typography>
              
              <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
                <Chip label={`Capacitate: ${shelter.capacity || 50} animale`} />
                <Chip label={`Ocupare: ${shelter.current_occupancy || 25} animale`} />
                <Chip label={`Zona: ${shelter.city}, ${shelter.county}`} />
              </Box>

              <Typography variant="h6" gutterBottom>
                Servicii oferite
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip label="Adopție" color="primary" />
                <Chip label="Foster temporar" color="primary" />
                <Chip label="Sterilizare" color="primary" />
                <Chip label="Consultanțe veterinare" color="primary" />
                <Chip label="Transport" color="primary" />
              </Box>
            </Paper>

            {/* Galerie media */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Galerie media
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <Box key={item} flex="1 1 200px" minWidth="200px">
                    <Card>
                      <CardMedia
                        component="img"
                        height="150"
                        image="/api/placeholder/300/150"
                        alt={`Foto ${item}`}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Card>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Animalele din adapost */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  Animalele din adapost ({animals.length})
                </Typography>
                <Button variant="outlined" startIcon={<Pets />}>
                  Vezi toate animalele
                </Button>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={2}>
                {animals.slice(0, 6).map((animal) => (
                  <Box key={animal.id} flex="1 1 300px" minWidth="300px">
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={animal.photos[0] || "/api/placeholder/300/200"}
                        alt={animal.name}
                      />
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {animal.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {animal.species} • {animal.breed} • {animal.gender || (animal as any).sex === 'M' ? 'Mascul' : 'Femelă'}
                        </Typography>
                        <Box display="flex" gap={1} mb={2}>
                          <Chip 
                            label={animal.status === 'available' ? 'Disponibil' : 'Rezervat'} 
                            color={animal.status === 'available' ? 'success' : 'warning'}
                            size="small"
                          />
                          {animal.sterilized && <Chip label="Sterilizat" size="small" />}
                          {animal.vaccinated && <Chip label="Vaccinat" size="small" />}
                        </Box>
                        <Box display="flex" gap={1}>
                          <Button size="small" variant="outlined">
                            Detalii
                          </Button>
                          <Button size="small" variant="contained">
                            Aplică pentru adopție
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Sidebar */}
          <Box flex={1}>
            {/* Program și locație */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Program și locație
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LocationOn color="primary" />
                <Typography variant="body2">
                  {shelter.address || `${shelter.city}, ${shelter.county}`}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Directions />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Deschide în Google Maps
              </Button>
              
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Schedule color="primary" />
                <Typography variant="body2">
                  Program: Luni - Duminică, 09:00 - 17:00
                </Typography>
              </Box>
            </Paper>

            {/* Contact rapid */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contact rapid
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Phone color="primary" />
                <Typography variant="body2">{shelter.phone || 'Nu este disponibil'}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Email color="primary" />
                <Typography variant="body2">{shelter.email || 'Nu este disponibil'}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Language color="primary" />
                <Typography variant="body2">{shelter.website || 'Nu este disponibil'}</Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Rețele sociale
              </Typography>
              <Box display="flex" gap={1}>
                <Button size="small" startIcon={<Facebook />}>Facebook</Button>
                <Button size="small" startIcon={<Instagram />}>Instagram</Button>
                <Button size="small" startIcon={<YouTube />}>YouTube</Button>
              </Box>
            </Paper>

            {/* Donează / Implică-te */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Donează / Implică-te
              </Typography>
              <Button
                variant="contained"
                startIcon={<Donate />}
                fullWidth
                sx={{ mb: 1 }}
              >
                Donează acum
              </Button>
              <Button
                variant="outlined"
                startIcon={<VolunteerActivism />}
                fullWidth
                sx={{ mb: 1 }}
              >
                Devino voluntar
              </Button>
              <Button
                variant="outlined"
                startIcon={<Pets />}
                fullWidth
              >
                Donează hrană / medicamente
              </Button>
            </Paper>
          </Box>
        </Box>
      </Container>

      {/* Dialog pentru programare vizită */}
      <Dialog open={visitDialogOpen} onClose={() => setVisitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Programează o vizită</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nume"
              value={visitForm.name}
              onChange={(e) => setVisitForm({ ...visitForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={visitForm.email}
              onChange={(e) => setVisitForm({ ...visitForm, email: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Telefon"
              value={visitForm.phone}
              onChange={(e) => setVisitForm({ ...visitForm, phone: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Data"
              type="date"
              value={visitForm.date}
              onChange={(e) => setVisitForm({ ...visitForm, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Ora"
              type="time"
              value={visitForm.time}
              onChange={(e) => setVisitForm({ ...visitForm, time: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitDialogOpen(false)}>Anulează</Button>
          <Button onClick={handleVisitSubmit} variant="contained">Trimite cererea</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pentru contact */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contactează adapostul</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nume"
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Mesaj"
              multiline
              rows={4}
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Anulează</Button>
          <Button onClick={handleContactSubmit} variant="contained">Trimite mesajul</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShelterPublicPage;
